import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

const TestModal = ({ open, onClose, onSubmit }) => {
  const emptyForm = {
    testName: '',
    priority: 'routine',
    clinicalIndication: '',
    frequency: '',
    scheduledDateTime: ''
  };

  const [formData, setFormData] = useState({ ...emptyForm });
  const [tests, setTests] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const formRef = useRef(null);

  const handleAddToList = (e) => {
    e.preventDefault();
    if (!formData.testName || !formData.priority) return;
    
    if (editingId) {
      // Update existing item
      setTests((prev) =>
        prev.map((t) =>
          t.id === editingId ? { ...t, ...formData } : t
        )
      );
      setEditingId(null);
    } else {
      // Add new item
      setTests((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          ...formData,
          addToTaskList: false,
        },
      ]);
    }
    setFormData({ ...emptyForm });
  };

  const handleToggleItemTaskList = (id) => {
    setTests((prev) =>
      prev.map((t) => (t.id === id ? { ...t, addToTaskList: !t.addToTaskList } : t))
    );
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleRemove = (id) => {
    setTests((prev) => prev.filter((t) => t.id !== id));
    if (editingId === id) {
      setEditingId(null);
      setFormData({ ...emptyForm });
    }
  };

  const handleEdit = (item) => {
    setFormData({
      testName: item.testName,
      priority: item.priority,
      clinicalIndication: item.clinicalIndication || '',
      frequency: item.frequency || '',
      scheduledDateTime: item.scheduledDateTime || ''
    });
    setEditingId(item.id);
  };

  useEffect(() => {
    if (editingId && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [editingId]);

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
  };

  const handleSaveCarePlan = () => {
    if (!tests.length) return;
    const hasTaggedItems = tests.some((t) => t.addToTaskList);
    onSubmit({ tests }, { addToTaskList: hasTaggedItems });
    setTests([]);
    setFormData({ ...emptyForm });
    onClose();
  };

  const handleClose = () => {
    setTests([]);
    setFormData({ ...emptyForm });
    setEditingId(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Test Order</DialogTitle>
      <form onSubmit={handleAddToList}>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Box ref={formRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Test Name *</Typography>
              <TextField
                fullWidth
                size="small"
                required
                placeholder="e.g., CBC, LFT, X-Ray Chest"
                value={formData.testName}
                onChange={handleChange('testName')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Priority *</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.priority}
                  onChange={handleChange('priority')}
                >
                  <MenuItem value="routine">Routine</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Clinical Indication <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Reason for ordering the test"
                value={formData.clinicalIndication}
                onChange={handleChange('clinicalIndication')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Frequency <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g., Daily, Once, Weekly"
                value={formData.frequency}
                onChange={handleChange('frequency')}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Scheduled Date/Time <Typography component="span" color="text.secondary">(Optional)</Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={formData.scheduledDateTime}
                onChange={handleChange('scheduledDateTime')}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {tests.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Tests in this care plan ({tests.length}) – tag to add to task list
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {tests.map((t) => (
                    <Paper
                      key={t.id}
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: t.addToTaskList ? 'primary.main' : 'divider',
                        backgroundColor: t.addToTaskList ? 'primary.50' : 'grey.50',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={t.addToTaskList}
                              onChange={() => handleToggleItemTaskList(t.id)}
                              sx={{ mt: -0.5 }}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              Add to task list
                            </Typography>
                          }
                          sx={{ mt: -0.5, mr: 1 }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t.testName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`Priority: ${t.priority.charAt(0).toUpperCase() + t.priority.slice(1)}${t.clinicalIndication ? ` • ${t.clinicalIndication}` : ''}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Tooltip title="Edit test">
                          <IconButton
                            size="small"
                            color={editingId === t.id ? 'primary' : 'default'}
                            onClick={() => handleEdit(t)}
                            aria-label="Edit test"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          aria-label="Remove test"
                          onClick={() => handleRemove(t.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined">Cancel</Button>
          {editingId && (
            <Button onClick={handleCancelEdit} variant="outlined">Cancel Edit</Button>
          )}
          <Button type="submit" variant="outlined">{editingId ? 'Update Item' : 'Add to List'}</Button>
          <Button
            onClick={handleSaveCarePlan}
            variant="contained"
            disabled={!tests.length}
          >
            Save Care Plan
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TestModal;
